import React, { useMemo } from 'react';
import useGameBase from '../../hooks/useGameBase';

const WINNING_LINES = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function createEmptyBoard() {
  return {
    cells: Array(9).fill(null),
    winner: null,
    winningLine: [],
  };
}

function boardIsPlayable(board) {
  return !!board && !board.winner && board.cells.some((cell) => !cell);
}

function getWinningLine(cells, symbol) {
  for (const line of WINNING_LINES) {
    if (line.every((index) => cells[index] === symbol)) {
      return line;
    }
  }
  return [];
}

function resolveStatusText(gameState, currentPlayerName, activeBoard, isMyTurn) {
  if (gameState.finished) {
    if (gameState.winnerSymbol === 'draw') return 'The game ended in a draw.';
    return `${currentPlayerName || 'A player'} won the macro board.`;
  }
  if (!gameState.currentPlayer) return 'Waiting for the next move.';
  const boardText = activeBoard === null ? 'any open board' : `board ${activeBoard + 1}`;
  return isMyTurn ? `Your turn. Play in ${boardText}.` : `${currentPlayerName || 'Opponent'} is playing in ${boardText}.`;
}

function resolveBoardBadge(board) {
  if (!board?.winner) return null;
  if (board.winner === 'draw') return 'DRAW';
  return board.winner;
}

function PlayerChip({ player, isActive }) {
  return (
    <div className={`flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 ${isActive ? 'border-sky-400 bg-sky-50 dark:border-sky-500 dark:bg-sky-500/10' : 'border-slate-200 bg-white/90 dark:border-slate-700 dark:bg-slate-900/90'}`}>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{player.username}</div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{player.symbol}</div>
      </div>
      <div className="text-right">
        <div className="text-lg font-black text-slate-900 dark:text-slate-100">{player.score || 0}</div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">wins</div>
      </div>
    </div>
  );
}

export default function UltimateTicTacToeGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction, isLocalPlayerTurn } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const boards = useMemo(() => {
    if (Array.isArray(gameState.boards) && gameState.boards.length === 9) return gameState.boards;
    return Array.from({ length: 9 }, createEmptyBoard);
  }, [gameState.boards]);
  const players = useMemo(() => gameState.players || gamePlayers || [], [gamePlayers, gameState.players]);
  const isMyTurn = !gameState.finished && isLocalPlayerTurn();
  const activeBoard = Number.isInteger(gameState.activeBoard) ? gameState.activeBoard : null;
  const winningBoardSet = useMemo(() => new Set(gameState.winningLine || []), [gameState.winningLine]);
  const currentPlayerName = useMemo(() => {
    const current = players.find((player) => player.socketId === gameState.currentPlayer);
    return current?.username || null;
  }, [gameState.currentPlayer, players]);
  const winnerName = useMemo(() => {
    const winnerId = Array.isArray(gameState.winners) ? gameState.winners[0] : null;
    const winner = players.find((player) => player.socketId === winnerId);
    return winner?.username || null;
  }, [gameState.winners, players]);
  const statusText = useMemo(() => resolveStatusText(gameState, gameState.finished ? winnerName : currentPlayerName, activeBoard, isMyTurn), [activeBoard, currentPlayerName, gameState, isMyTurn, winnerName]);

  const handleCellClick = async (boardIndex, cellIndex) => {
    const board = boards[boardIndex];
    if (!isMyTurn || !boardIsPlayable(board)) return;
    if (board.cells[cellIndex]) return;
    if (activeBoard !== null && activeBoard !== boardIndex) return;
    await submitAction({
      type: 'place',
      boardIndex,
      cellIndex,
    });
  };

  return (
    <div className="mx-auto mt-4 flex w-full max-w-5xl flex-col gap-4 text-slate-900 dark:text-slate-100">
      <div className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Ultimate Tic Tac Toe</div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{statusText}</div>
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {activeBoard === null ? 'Any open board' : `Forced board ${activeBoard + 1}`}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {players.slice(0, 2).map((player) => (
            <PlayerChip key={player.socketId} player={player} isActive={gameState.currentPlayer === player.socketId && !gameState.finished} />
          ))}
        </div>
      </div>

      <div className="mx-auto w-full max-w-[min(96vw,32rem)] rounded-[2rem] border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:to-slate-950">
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {boards.map((board, boardIndex) => {
            const boardPlayable = boardIsPlayable(board);
            const boardForced = activeBoard !== null && boardIndex === activeBoard;
            const boardSelectable = activeBoard === null ? boardPlayable : boardForced;
            const boardWon = !!board.winner;
            const boardWinningLine = board.winningLine?.length ? board.winningLine : getWinningLine(board.cells, board.winner);
            const boardBadge = resolveBoardBadge(board);
            const macroWon = winningBoardSet.has(boardIndex);

            return (
              <div
                key={boardIndex}
                className={`relative aspect-square rounded-2xl border p-1.5 sm:p-2 ${macroWon ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-500/10' : boardForced ? 'border-sky-400 bg-sky-50 dark:border-sky-500 dark:bg-sky-500/10' : boardSelectable ? 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900' : 'border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-950/80'}`}
              >
                <div className={`grid h-full grid-cols-3 gap-1 ${boardSelectable ? 'opacity-100' : 'opacity-70'}`}>
                  {board.cells.map((cell, cellIndex) => {
                    const cellDisabled = !!cell || boardWon || !isMyTurn || !boardSelectable || gameState.finished;
                    const winningCell = boardWinningLine?.includes(cellIndex);
                    return (
                      <button
                        key={`${boardIndex}-${cellIndex}`}
                        type="button"
                        onClick={() => handleCellClick(boardIndex, cellIndex)}
                        disabled={cellDisabled}
                        className={`flex aspect-square items-center justify-center rounded-xl border text-lg font-black transition sm:text-2xl ${winningCell ? 'border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/20 dark:text-emerald-200' : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'} ${!cellDisabled ? 'hover:border-sky-400 hover:bg-sky-50 dark:hover:border-sky-500 dark:hover:bg-sky-500/10' : ''}`}
                      >
                        {cell === 'X' ? <span className="text-sky-600 dark:text-sky-300">X</span> : null}
                        {cell === 'O' ? <span className="text-pink-600 dark:text-pink-300">O</span> : null}
                      </button>
                    );
                  })}
                </div>
                {boardBadge ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-white/55 text-3xl font-black text-slate-800 backdrop-blur-[1px] dark:bg-slate-950/50 dark:text-slate-100 sm:text-4xl">
                    {boardBadge}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

UltimateTicTacToeGame.Guide = function UltimateTicTacToeGuide() {
  return (
    <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
      <h3 className="font-bold">How to Play Ultimate Tic Tac Toe</h3>
      <ul className="list-disc space-y-1 pl-5">
        <li>Each move is played inside one of the nine mini tic-tac-toe boards.</li>
        <li>The cell you choose sends your opponent to the matching mini-board for their next move.</li>
        <li>If that target mini-board is already won or full, the next player may play in any open board.</li>
        <li>Win a mini-board to claim it on the macro board.</li>
        <li>Claim three mini-boards in a row on the macro board to win the game.</li>
      </ul>
    </div>
  );
};
